import { useState } from 'react';
import {
    AutocompleteArrayInput,
    EditBase,
    ReferenceArrayInput,
    TabbedForm,
    SaveButton,
    Toolbar,
    useEditContext,
} from 'react-admin';
import { Card, Box, Typography } from '@mui/material';

import { UserInputs } from './UserInputs';
import { UserCreditCardInputs } from './UserCreditCardInputs';
import { UserAside } from './UserAside';
import { User } from '@/types';
import { findDuplicateLastFour } from './userCreditCardValidation';

export const UserEdit = () => (
    <EditBase
        mutationMode="pessimistic"
        redirect="show"
        queryOptions={{ meta: { includes: 'user_credit_cards' } }}
    >
        <UserEditContent />
    </EditBase>
);

const UserEditContent = () => {
    const [isEditPasswordMode, setEditPasswordMode] = useState(false);
    const { isPending, record } = useEditContext<User>();

    if (isPending || !record) return null;

    const validateUserUpdate = (values: any) => {
        const errors: { [key: string]: string } = {};

        if (isEditPasswordMode) {
            if (!values.password) {
                errors.password = 'New password is required';
            }
            if (values.password && values.password !== values.confirm_password) {
                errors.confirm_password = 'The two passwords must match';
            }
            if (values.password && values.password.length < 8) {
                errors.password = 'Password must be at least 8 characters long';
            }
            if (values.password && values.password.length > 20) {
                errors.password = 'Password must be at most 20 characters long';
            }
            if (values.password && !/[0-9]/.test(values.password)) {
                errors.password = 'Password must contain at least one number';
            }
            if (values.password && !/[A-Z]/.test(values.password)) {
                errors.password = 'Password must contain at least one uppercase letter';
            }
            if (values.password && !/[a-zA-Z]/.test(values.password)) {
                errors.password = 'Password must contain at least one letter';
            }
        }

        // Check for duplicate credit cards
        const duplicateCard = findDuplicateLastFour(values.credit_cards);
        if (duplicateCard) {
            errors.credit_cards = `Card ending ${duplicateCard} is duplicated in the list`;
        }

        return errors;
    };

    return (
        <Box mt={2} display="flex">
            <UserAside link="show" />
            <Box flex="1">
                <Card>
                    {/* @ts-ignore */}
                    <TabbedForm
                        validate={validateUserUpdate}
                        toolbar={
                            <Toolbar>
                                <SaveButton />
                            </Toolbar>
                        }
                    >
                        <TabbedForm.Tab label="Basic Info">
                            <UserInputs
                                isEditMode={true}
                                isEditPasswordMode={isEditPasswordMode}
                                setEditPasswordMode={setEditPasswordMode}
                            />
                        </TabbedForm.Tab>
                        <TabbedForm.Tab label="Credit Cards" path="credit-cards">
                            <UserCreditCardInputs />
                        </TabbedForm.Tab>
                        <TabbedForm.Tab label="Receipt helper" path="receipt-helper">
                            <Typography variant="body2" color="text.secondary">
                                This user can see, upload, match and post receipts for these coworkers.
                                Only an admin verifies and sends to QuickBooks.
                            </Typography>
                            <ReferenceArrayInput
                                source="helps_user_ids"
                                reference="users"
                                sort={{ field: 'name', order: 'ASC' }}
                                perPage={200}
                            >
                                <AutocompleteArrayInput
                                    label="Can help with"
                                    optionText="name"
                                    sx={{ minWidth: 360 }}
                                />
                            </ReferenceArrayInput>
                        </TabbedForm.Tab>
                    </TabbedForm>
                </Card>
            </Box>
        </Box>
    );
};
